<template>
  <tab-page-template>
    <portal to="pages_profile_ProfileManagement_DataProfile_CreateButtons">
      <!-- 起案申請型の取引契約を開始 -->
      <template v-if="isOwnedDataProfile">
        <v-btn
            color="primary"
            class="ml-3 px-4"
            @click="openCreateDialog('produce', 'draft')"
            v-if="['common', 'produce'].includes(dataProfileView.serviceType)">
          <span class="mr-2">提供する</span>
          <v-icon>mdi-briefcase-upload</v-icon>
        </v-btn>
        <v-btn
            color="primary"
            class="ml-3 px-4"
            @click="openCreateDialog('consume', 'draft')"
            v-if="['common', 'consume'].includes(dataProfileView.serviceType)">
          <span class="mr-2">利用する</span>
          <v-icon>mdi-briefcase-download</v-icon>
        </v-btn>
      </template>

      <!-- 申込申請型の取引契約を開始 -->
      <template v-else>
        <v-btn
            color="primary"
            class="ml-3 px-4"
            @click="openCreateDialog('consume', 'offer')"
            v-if="['common', 'produce'].includes(dataProfileView.serviceType)">
          <span class="mr-2">利用する</span>
          <v-icon>mdi-briefcase-download</v-icon>
        </v-btn>
        <v-btn
            color="primary"
            class="ml-3 px-4"
            @click="openCreateDialog('produce', 'offer')"
            v-if="['common', 'consume'].includes(dataProfileView.serviceType)">
          <span class="mr-2">提供する</span>
          <v-icon>mdi-briefcase-upload</v-icon>
        </v-btn>
      </template>
    </portal>

    <template #toolbar-items>
      <v-toolbar-title class="with-divider">
        データプロファイル閲覧
      </v-toolbar-title>
      <v-spacer></v-spacer>
      <portal-target
          v-if="!showCreateMessage"
          name="pages_profile_ProfileManagement_DataProfile_CreateButtons"
      />
      <slot name="toolbar-items"></slot>
    </template>

    <slot name="owner-info">
      <owner-info
          v-if="!isOwnedDataProfile"
          :account="ownerInfo"
          show-detail>
      </owner-info>
    </slot>

    <v-container fluid>
      <data-profile-info
        :data-profile-view="dataProfileView">
      </data-profile-info>
    </v-container>

    <template #tab-items>
      <v-tab>データ項目プロファイル</v-tab>
      <v-tab-item>
        <data-property-profile-tab :data-profile-id="dataProfileId"></data-property-profile-tab>
      </v-tab-item>
    </template>

    <simple-dialog
        :value="createDialog.visible"
        :title-text="`${convertCreateDialogModeForDisplay(createDialog.mode)}の作成`"
        @close="closeCreateDialog"
        no-padding>
      <v-container class="mb-0 pb-0 text-left" v-if="!hideCreateModeToggle">
        <div class="v-messages theme--light">
          作成の種別
        </div>
        <v-btn-toggle
            v-model="createDialog.mode"
            color="blue darken-4"
            mandatory
        >
          <v-btn value="tradeContract">
            <v-icon class="mr-1">icon-trade-contact</v-icon>取引契約
          </v-btn>
          <v-btn value="tradeContractFormat">
            <v-icon class="mr-1">mdi-folder-lock-outline</v-icon>取引契約フォーマット
          </v-btn>
        </v-btn-toggle>
      </v-container>
      <create-trade-contract-format-form
          v-if="createDialog.mode === 'tradeContractFormat'"
          v-model="createDialog.data"
          :service-type="createDialog.serviceType"
          :initial-contract-type="createDialog.initialContractType"
      >
      </create-trade-contract-format-form>
      <create-trade-contract-form
          v-if="createDialog.mode === 'tradeContract'"
          v-model="createDialog.data"
          :service-type="createDialog.serviceType"
          :initial-contract-type="createDialog.initialContractType"
          :initial-target-terminal-id="createDialog.initialTargetTerminalId">
        <template #after-basic-form v-if="!isOwnedDataProfile">
          <v-row>
            <v-col cols="6">
              <text-field-like
                :value="ownerInfo.ownerName"
                :value-id="ownerInfo.ownerId"
                label="取引先事業者名">
              </text-field-like>
            </v-col>
            <v-col cols="6">
              <text-field-like
                  :value="ownerInfo.terminalName"
                  :value-id="ownerInfo.terminalId"
                  label="取引先サイト名">
              </text-field-like>
            </v-col>
          </v-row>
        </template>
      </create-trade-contract-form>
    </simple-dialog>

    <app-header :errors="messages" v-if="!!showCreateMessage">
      <error-panel :errors="messages" small></error-panel>
      <v-spacer></v-spacer>
      <portal-target name="pages_profile_ProfileManagement_DataProfile_CreateButtons"></portal-target>
    </app-header>
  </tab-page-template>
</template>

<script>
import TabPageTemplate from "@/components/templates/TabPageTemplate.vue";
import OwnerInfo from "@/components/organisms/OwnerInfo.vue";
import {mapGetters, mapMutations} from "vuex";
import IoablePageMixin from "@/router/IoablePageMixin";
import DataPropertyProfileTab from "@/components/pages/profile/ProfileManagement/DataProfile/DataPropertyProfileTab.vue";
import SimpleDialog from "@/components/organisms/SimpleDialog.vue";
import CreateTradeContractForm from "@/components/pages/profile/ProfileManagement/DataProfile/CreateTradeContractForm.vue";
import DataProfileInfo from "@/components/organisms/DataProfileInfo.vue";
import DataProfileSource from "@/components/organisms/DataProfileSource";
import {createTradeContractView} from "@/components/pages/contract/TradeContract/TradeContractView";
import {safeMeta} from "@/router/Meta";
import {DateTime} from "luxon";
import TextFieldLike from "@/components/molecules/TextFieldLike.vue";
import CreateTradeContractFormatForm
  from "@/components/pages/profile/ProfileManagement/DataProfile/CreateTradeContractFormatForm.vue";
import {createTradeContractFormatView} from "@/components/pages/contract/TradeContractFormat/TradeContractFormatView";
import AppHeader from "@/components/atoms/AppHeader.vue";
import ErrorPanel from "@/components/molecules/ErrorPanel.vue";

export default {
  name: "DataProfile",
  components: {
    ErrorPanel,
    AppHeader,
    TextFieldLike,
    DataProfileInfo,
    CreateTradeContractForm,
    CreateTradeContractFormatForm,
    SimpleDialog, DataPropertyProfileTab, OwnerInfo, TabPageTemplate},
  mixins: [IoablePageMixin, DataProfileSource],
  props: {
    dataProfileId: {
      type: String,
      required: true
    },
    initialCreateMode: {
      type: String, // "tradeContract" or "tradeContractFormat"
      default: 'tradeContract'
    },
    hideCreateModeToggle: {
      type: Boolean,
      default: false,
    },
    showCreateMessage: {
      type: String,
      default: '',
    }
  },
  data() {
    return {
      dataProfileView: {},
      createDialog: {
        visible: false,
        mode: null, // "tradeContract" or "tradeContractFormat"
        data: false, // tradeContract or tradeContractFormat
        serviceType: '',
        initialContractType: 'draft',
        initialTargetTerminalId: null,
      },
    }
  },
  computed: {
    ...mapGetters({
      account: 'hcs/account',
      hcsClient: 'hcs/client',
    }),
    isOwnedDataProfile() {
      return safeMeta(this.$route).isOwnedDataProfile ?? false;
    },
    ownerInfo() {
      if(this.isOwnedDataProfile) {
        return this.account;
      } else {
        const dp = this.dataProfileView;
        return {
          ownerId: dp.ownerId,
          ownerName: dp.ownerName,
          terminalId: dp.terminalId,
          terminalName: dp.terminalName,
        };
      }
    },
    messages() {
      return [{
        level: 'good',
        message: this.showCreateMessage
      }];
    },
  },
  methods: {
    ...mapMutations({
      showSnackbar: 'showSnackbar'
    }),
    async load() {
      this.dataProfileView = this.isOwnedDataProfile ?
          await this.loadDataProfile(this.dataProfileId) :
          await this.loadAssetsDataProfile(this.dataProfileId) ;
    },
    convertCreateDialogModeForDisplay(mode) {
      switch(mode) {
        case 'tradeContract': return '取引契約';
        case 'tradeContractFormat': return '取引契約フォーマット';
        default: return '';
      }
    },
    openCreateDialog(serviceType, initialContractType) {
      this.createDialog = {
        ...this.createDialog,
        visible: true,
        mode: this.initialCreateMode,
        data: false,
        serviceType,
        initialContractType,
        initialTargetTerminalId: this.isOwnedDataProfile ? null : this.dataProfileView.terminalId,
      };
    },
    async closeCreateDialog(apply) {
      if(apply) {
        const mode = this.createDialog.mode;
        const modeDisplay = this.convertCreateDialogModeForDisplay(mode);
        const tradeContractLike = this.createDialog.data;
        tradeContractLike.commonDictionaryId = this.dataProfileView.commonDataComponentModel.dictionary.id;
        const dataProfileProperty = (() => {
          switch (tradeContractLike.serviceType) {
            case 'produce':
              return this.isOwnedDataProfile ? 'producerDataProfileId' : 'consumerDataProfileId';
            case 'consume':
              return this.isOwnedDataProfile ? 'consumerDataProfileId' : 'producerDataProfileId';
          }
        })();
        tradeContractLike[dataProfileProperty] = this.dataProfileId;

        const progressSnackbar = () => {
          this.showSnackbar({
            message: `${modeDisplay} ${tradeContractLike.name} の作成中...`,
            timeout: false,
            loading: true
          });
        }
        const doneSnackbar = () => {
          this.showSnackbar({
            message: `${modeDisplay} ${tradeContractLike.name} を作成しました`,
          });
        };
        const errorSnackbar = (e) => {
          console.error(e);
          this.showSnackbar({
            color: 'failed',
            message: `${modeDisplay} ${tradeContractLike.name} の作成に失敗しました`,
          });
        };


        // 取引契約作成モード
        if(this.createDialog.mode === 'tradeContract') {
          const view = createTradeContractView(tradeContractLike, this.$store);

          // ステータスは「編集中」
          view.status = 'preparing';

          // 回答期限は30日後とする
          view.expireDate = DateTime.now().startOf('day').plus({days: 30});

          try {
            // 取引契約の作成
            progressSnackbar();
            const {data: result} = await this.hcsClient.post('/trade_contracts', view.buildPostData());
            doneSnackbar();

            // 取引起案画面に遷移
            await this.$router.push({
              name: (() => {
                switch (view.contractType) {
                  case 'draft':
                    return '/draftContract';
                  case 'recruitment':
                    return '/publishContract';
                  case 'offer':
                    return '/offerContract';
                }
              })(),
              params: {
                tradeContractId: result.id
              }
            });
          } catch (e) {
            errorSnackbar(e);
          }
        }

        // 取引契約フォーマット作成モード
        if(this.createDialog.mode === 'tradeContractFormat') {
          const view = createTradeContractFormatView(tradeContractLike, this.$store);

          try {
            // 取引契約フォーマットの作成
            progressSnackbar();
            const {data: result} = await this.hcsClient.post('/trade_contract_formats', view.buildPostData());
            doneSnackbar();

            // 取引契約フォーマット編集画面に遷移
            await this.$router.push({
              name: '/edit/tradeFormat',
              params: {
                tradeContractFormatId: result.id
              }
            });
          } catch (e) {
            errorSnackbar(e);
          }
        }
      }
      this.createDialog.visible = false;
      this.createDialog.data = false;
    },
  },
}
</script>

<style scoped>

</style>
